import { Box, Checkbox, Dialog, FormControlLabel, IconButton, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { createApiKey } from 'entities/ApiKeys/api';
import { CreateApiKeyResponse } from 'entities/ApiKeys/types';
import { Form, Formik, FormikProps } from 'formik';
import { enqueueSnackbar } from 'notistack';
import { FunctionComponent, useState } from 'react';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import { ColorPalette } from 'shared/consts/colorPalette';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { ActionsForDialog } from 'shared/ui/ActionsForDialog';
import { Button } from 'shared/ui/Button';
import { Icon } from 'shared/ui/Icon';
import { InputField } from 'shared/ui/InputField';
import { Paper } from 'shared/ui/Paper';
import { CreateApiKeyValidationSchema } from '../../consts/validation';

interface Props {
	open: boolean;
	onClose: () => void;
	onSuccess: () => void;
}

interface FormValues {
	name: string;
}

const CreateApiKeyModal: FunctionComponent<Props> = ({ open, onClose, onSuccess }) => {
	const { t } = useTranslation();
	const [createdKey, setCreatedKey] = useState<CreateApiKeyResponse | null>(null);
	const [confirmed, setConfirmed] = useState(false);

	const { mutate, isLoading } = useMutation({
		mutationFn: createApiKey,
		onSuccess: (res) => {
			setCreatedKey(res.data);
		},
		onError: (err: AxiosError) => {
			showErrorNotification(err);
		},
	});

	const handleSubmit = (values: FormValues) => {
		mutate({ name: values.name.trim() });
	};

	const handleCopy = () => {
		if (!createdKey?.rawKey) return;
		navigator.clipboard.writeText(createdKey.rawKey);
		enqueueSnackbar({ message: t('public_api_keys.key_copied'), variant: 'success' });
	};

	const handleClose = () => {
		if (createdKey) {
			onSuccess();
		}
		setCreatedKey(null);
		setConfirmed(false);
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			PaperComponent={Paper}
			PaperProps={{
				title: createdKey
					? (t('public_api_keys.save_key_title') as string)
					: (t('public_api_keys.create_modal_title') as string),
			}}
		>
			{!createdKey ? (
				<Formik
					initialValues={{ name: '' }}
					validationSchema={CreateApiKeyValidationSchema}
					onSubmit={handleSubmit}
				>
					{({ errors, values }: FormikProps<FormValues>) => (
						<Form>
							<InputField
								name={'name'}
								label={t('public_api_keys.key_name_label')}
								placeholder={t('public_api_keys.key_name_placeholder')}
								required
							/>

							<ActionsForDialog
								cancelLabel={t('shared_texts.cancel')}
								cancelAction={handleClose}
								applyLabel={t('public_api_keys.create_key_btn')}
								applyActionDisabled={!!errors.name || !values.name.trim() || isLoading}
							/>
						</Form>
					)}
				</Formik>
			) : (
				<Box>
					<Box
						sx={{
							background: ColorPalette.offYellow,
							border: `1px solid ${ColorPalette.fuelYellow}`,
							borderRadius: '8px',
							padding: '12px 16px',
							marginBottom: '16px',
						}}
					>
						<Typography variant={'body2'} color={ColorPalette.astronaut} fontWeight={600}>
							{t('public_api_keys.save_key_warning')}
						</Typography>
					</Box>
					<Typography variant={'body2'} color={ColorPalette.baliHai} sx={{ marginBottom: '12px' }}>
						{t('public_api_keys.save_key_description')}
					</Typography>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: '8px',
							backgroundColor: ColorPalette.catskillWhite,
							borderRadius: '8px',
							padding: '12px 16px',
							marginBottom: '20px',
						}}
					>
						<Typography
							variant={'body2'}
							color={ColorPalette.astronaut}
							sx={{
								flex: 1,
								wordBreak: 'break-all',
								fontFamily: 'monospace',
								fontSize: '13px',
							}}
						>
							{createdKey.rawKey}
						</Typography>
						<IconButton onClick={handleCopy} size={'small'}>
							<Icon
								id={'copy'}
								style={{ width: '18px', height: '18px', color: ColorPalette.royalBlue }}
							/>
						</IconButton>
					</Box>
					<FormControlLabel
						sx={{ marginBottom: '24px', alignItems: 'center' }}
						control={
							<Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
						}
						label={
							<Typography variant={'body2'} color={ColorPalette.astronaut}>
								{t('public_api_keys.save_key_confirm')}
							</Typography>
						}
					/>
					<Button fullWidth variant={'contained'} disabled={!confirmed} onClick={handleClose}>
						{t('shared_texts.close')}
					</Button>
				</Box>
			)}
		</Dialog>
	);
};

export default CreateApiKeyModal;
